import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';

import { FsStore } from '@firestitch/store';

import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { EnvironmentFilters } from '@app/common';
import { WindowMessageEventType } from '@app/common/enums';
import { Account, Environment } from '@app/common/interfaces';

import { WindowMessageService } from './window-message.service';


@Injectable({
  providedIn: 'root',
})
export class SessionService implements OnDestroy {
  private _store = inject(FsStore);
  private _router = inject(Router);
  private _windowMessageSerice = inject(WindowMessageService);


  private _destroy$ = new Subject<void>();

  public set(data: any): void {
    this._store.set('account', data.account);
    this._store.set('permissions', data.permissions);
    this._store.set('environment', data.environment);
  }

  public destroy(): void {
    this._windowMessageSerice.broadcast(WindowMessageEventType.SessionDestroy);
    this._store.clear();
  }

  public init(): void {
    this._windowMessageSerice.event$
      .pipe(
        filter((event) => event.type === WindowMessageEventType.SessionDestroy),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._router.navigateByUrl('/signin');
      });
  }

  public recentObjectIds(data: number[] = null): number[] {
    if (data) {
      this._store.set('recentObjectIds', data);
    }
    const recentObjectIds = this._store.get('recentObjectIds');

    return recentObjectIds ? recentObjectIds : [];
  }

  public account(data: Account = null): Account {
    if (data) {
      this._store.set('account', data);
    }

    return this._store.get('account');
  }

  public accountId(): number {
    return (this.account() || {}).id;
  }

  public accountExists(): boolean {
    return !!this.account();
  }

  public isSessionAccount(account: Account): boolean {
    return account && this.accountId() === account.id;
  }

  public environment(data: Environment = null): Environment {
    if (data) {
      this._store.set('environment', data);
    }

    return this._store.get('environment');
  }

  public filterPersist(data = null): any {
    if (data) {
      this._store.set('fs-filter-persist', data);
    }

    return this._store.get('fs-filter-persist');
  }

  public permissions(data = null): any {
    if (data) {
      this._store.set('permissions', data);
    }

    return this._store.get('permissions');
  }

  public environmentId(): number {
    return this.environment() ? this.environment().id : null;
  }

  /**
   * Keeps the session's copy of its organization current after that organization is saved.
   * An admin saves any organization, with another one or none in session: a saved
   * organization that is not the session's never takes its place, since only a switch does that.
   */
  public refreshEnvironment(environment: Environment): void {
    if (this.environmentId() === environment.id) {
      this.environment(environment);
    }
  }

  public environmentFundFilters() {
    return this.environment() ? EnvironmentFilters
      .reduce((accum, item: { name: string; value: string }) => {
        return {
          ...accum,
          [item.value]: this.environment()[item.value] || false,
        };
      },{}) : [];
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
